import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';

export type ExecutionStatusBreakdown = {
  terminated: number;
  // Sherlock raised a stop but Watson successfully rescued the run. Reported
  // separately from `terminated` so a recovered execution isn't blamed on
  // the agent — the work actually got done.
  rescued: number;
  failed: number;
  warning: number;
  passed: number;
  // Successful executions captured while the agent was in LEARN mode.
  // Reported separately so they're not misclassified as Passed/Warning/Failed.
  learning: number;
  unapproved: number;
};

export type SherlockAgentStatus = {
  agent_id: string;
  internal_version: number;
  sherlock_enabled: boolean;
  current_mode: 'learn' | 'observer' | 'enforce';
  approved_executions: number;
  total_executions: number;
  anomalies_detected: number;
  executions_stopped: number;
  sensitivity_multiplier: number;
  auto_reject_enabled: boolean;
  needs_retrain: boolean;
  avg_quality_score: number | null;
  execution_status_breakdown: ExecutionStatusBreakdown;
  rebased_from_version: number | null;
  // Mode thresholds + remaining-to-next-mode counters. Backend computes
  // remaining from approved_execution_count, which carries over from a
  // rebased source version, so a rebased agent visibly inherits its
  // progress on the LEARN→OBSERVER ladder.
  learn_mode_threshold: number;
  observer_mode_threshold: number;
  learn_mode_remaining: number;
  observer_mode_remaining: number;
};

export type SherlockEnableRequest = {
  sensitivity_multiplier?: number;
  learn_mode_threshold?: number;
  observer_mode_threshold?: number;
};

export type SherlockEnableResponse = {
  agent_id: string;
  internal_version: number;
  sherlock_enabled: boolean;
  message: string;
};

export type SherlockSensitivityRequest = {
  multiplier: number;
};

export type SherlockSensitivityResponse = {
  agent_id: string;
  internal_version: number;
  sensitivity_multiplier: number;
  message: string;
};

export function getSherlockAgentStatus({
  agentId,
  internalVersion,
}: {
  agentId: string;
  internalVersion: number;
}) {
  return async () => {
    const res = await http.get<SherlockAgentStatus>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${internalVersion}/status`
    );
    return res.data;
  };
}

getSherlockAgentStatus.key = ({
  agentId,
  internalVersion,
}: {
  agentId: string;
  internalVersion: number;
}) => ['opal:sherlock-agent-status', agentId, internalVersion] as const;

export function enableSherlock({
  agentId,
  internalVersion,
  request = {},
}: {
  agentId: string;
  internalVersion: number;
  request?: SherlockEnableRequest;
}) {
  return async () => {
    const res = await http.post<SherlockEnableResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${internalVersion}/enable`,
      request
    );
    return res.data;
  };
}

export function updateSherlockSensitivity({
  agentId,
  internalVersion,
  request,
}: {
  agentId: string;
  internalVersion: number;
  request: SherlockSensitivityRequest;
}) {
  return async () => {
    const res = await http.put<SherlockSensitivityResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${internalVersion}/sensitivity`,
      request
    );
    return res.data;
  };
}

export type SherlockRejectRequest = {
  reason?: string;
};

export type SherlockRejectResponse = {
  execution_id: string;
  rejected: boolean;
  message: string;
};

export type SherlockApproveResponse = {
  execution_id: string;
  approved: boolean;
  message: string;
};

export function rejectSherlockExecution({
  executionId,
  request = {},
}: {
  executionId: string;
  request?: SherlockRejectRequest;
}) {
  return async () => {
    const res = await http.post<SherlockRejectResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/executions/${executionId}/reject`,
      request
    );
    return res.data;
  };
}

export function approveSherlockExecution({ executionId }: { executionId: string }) {
  return async () => {
    const res = await http.post<SherlockApproveResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/executions/${executionId}/approve`,
      {}
    );
    return res.data;
  };
}

export type SherlockAutoTerminateRequest = {
  enabled: boolean;
};

export type SherlockAutoTerminateResponse = {
  agent_id: string;
  internal_version: number;
  auto_reject_enabled: boolean;
  message: string;
};

export function updateSherlockAutoTerminate({
  agentId,
  internalVersion,
  request,
}: {
  agentId: string;
  internalVersion: number;
  request: SherlockAutoTerminateRequest;
}) {
  return async () => {
    const res = await http.put<SherlockAutoTerminateResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${internalVersion}/auto-terminate`,
      request
    );
    return res.data;
  };
}

export type SherlockRebaseModelRequest = {
  source_version: number;
  override_existing: boolean;
};

export type SherlockRebaseModelResponse = {
  agent_id: string;
  target_version: number;
  source_version: number;
  model_rebased: boolean;
  message: string;
};

export function rebaseSherlockModel({
  agentId,
  targetVersion,
  request,
}: {
  agentId: string;
  targetVersion: number;
  request: SherlockRebaseModelRequest;
}) {
  return async () => {
    const res = await http.post<SherlockRebaseModelResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${targetVersion}/rebase-model`,
      request
    );
    return res.data;
  };
}

export type SherlockClearRebaseResponse = {
  agent_id: string;
  internal_version: number;
  message: string;
};

export function clearSherlockRebase({
  agentId,
  internalVersion,
}: {
  agentId: string;
  internalVersion: number;
}) {
  return async () => {
    const res = await http.delete<SherlockClearRebaseResponse>(
      `${HYPATIA_URL}/api/v1/sherlock/agents/${agentId}/versions/${internalVersion}/rebase-model`
    );
    return res.data;
  };
}
